import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, EMPTY, exhaustMap, filter, map, of, Subject, Subscription, switchMap, tap } from 'rxjs';
import { ProjectService } from '../shared/services/project.service';
import { IProjectDetails } from '../shared/models/project-details';
import { SdBallotService } from '../shared/services/sd-ballot.service';
import { ISdBallotVote } from '../shared/models/sd-ballot-vote';
import { differenceInCalendarDays, isValid } from 'date-fns';
import { appConstants } from '../../../shared/constants/app-constants';
import { CommitteeMemberRoles } from '../../../shared/constants/committee-constants';
import { IUser } from '../../../shared/models/user';
import { AuthService } from '../../../shared/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { tryParseInt } from '../../../shared/utilities/number-utility';
import { IOption } from '../../controls/models/option';
import { RadioButtonLayoutDirection } from '../../controls/radio-group/radio-group-constants';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ICoaPreferenceRequest } from '../shared/models/coa-preference-request';
import { IApiResponse } from '../../../shared/models/api-response';
import { AlertStyle } from '../../controls/alert/alert-enums';


@Component({
  selector: 'app-certificate-of-appreciation',
  standalone: false,
  templateUrl: './certificate-of-appreciation.component.html',
  styleUrl: './certificate-of-appreciation.component.scss'
})
export class CertificateOfAppreciationComponent implements OnInit, OnDestroy {
  
  subscriptions: Subscription = new Subscription();
  submit$: Subject<boolean> = new Subject<boolean>();
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  currentUser: IUser | null = null;
  projectDetails: IProjectDetails | null = null;
  ballotVotes: ISdBallotVote[] = [];

  RadioButtonLayoutDirection = RadioButtonLayoutDirection; // Expose enum to template
  alertStyle = AlertStyle;

  coaPreferenceOptions: IOption<string>[] = [
    { value: 'CERTIFICATE_ONLY', label: 'Printed hardcopy of Certificate of Appreciation only' },
    { value: 'PUBLICATION_ONLY', label: 'Printed hardcopy of final publication only' },
    { value: 'BOTH', label: 'Printed hardcopy of Certificate of Appreciation and final publication' }
  ];

  hasPermissionToViewCoaPage: boolean | null = null;
  isExpired: boolean | null = null; // Users have a limited time to enter the CoA page after Project is published.

  coaForm = new FormGroup({
    certificatePreferenceCtrl: new FormControl<string | null>(null, Validators.required),
    salutationCtrl: new FormControl<string | null>(null, Validators.required),
    firstNameCtrl: new FormControl<string | null>(null, Validators.required),
    lastNameCtrl: new FormControl<string | null>(null, Validators.required),
    addressCtrl: new FormControl<string | null>(null, Validators.required),
    apartmentSuiteCtrl: new FormControl<string | null>(null),
    suburbCityCtrl: new FormControl<string | null>(null, Validators.required),
    stateCtrl: new FormControl<string | null>(null, Validators.required),
    postcodeCtrl: new FormControl<string | null>(null, Validators.required),
    countryCtrl: new FormControl<string | null>(null, Validators.required),
  });

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _authService: AuthService,
    private _projectService: ProjectService,
    private _sdBallotService: SdBallotService,
    private _toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.setupActivatedRouteSubscriptions();
    this.setupSubmitSubscription();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setupActivatedRouteSubscriptions() {
    const projectId$ = this._activatedRoute.paramMap.pipe(
      tap(params => this.resetPageAccess()),
      tap(params => this.isLoading = true),
      map(params => tryParseInt(params.get('id'), null)),
      filter((projectId): projectId is number => projectId !== null)
    );

    const projectAndUser$ = projectId$.pipe(
      switchMap((projectId) => this._authService.currentUserSubject.pipe(
        filter((user): user is IUser => user !== null),
        map((user) => ({ projectId, user }))
      ))
    );

    const projectDetails$ = projectAndUser$.pipe(
      switchMap(({ projectId, user }) =>
        this._projectService.getProjectDetails(projectId).pipe(
          map(projectDetailsResponse => ({ user, projectDetailsResponse })),
          tap(({ user, projectDetailsResponse }) => {
            if (!projectDetailsResponse?.success || projectDetailsResponse.content === null) {
              console.error('Failed to load project details for certificate of appreciation', {
                projectId,
                response: projectDetailsResponse?.message
              });
            }
          }),
          catchError((err) => {
            console.error('Certificate of Appreciation: Error occurred while loading project', err);
            this._toastService.showError("Error retrieving project. Please try again or contact IT Support if issue persists.", "Certificate of Appreciation");
            return EMPTY;
          })
        )
      )
    );

    const sdBallotVotes$ = projectDetails$.pipe(
      switchMap(({ user, projectDetailsResponse }) => {
         return this._sdBallotService.getVotesByProjectId(projectDetailsResponse.content.projectId).pipe(
          map(votesResponse => ({ user, projectDetailsResponse, votesResponse })),
          tap(({ user, projectDetailsResponse, votesResponse }) => {
            if(!votesResponse?.success || votesResponse.content === null) {
              console.error('Certificate of Appreciation: Failed to load SD ballot votes', {
                sdBallotId: projectDetailsResponse.content.sdBallotId,
                response: votesResponse?.message
              });
            }
          }),
          catchError((err) => {
            console.error('Certificate of Appreciation: Error occurred while loading SD ballot votes', err);
            this._toastService.showError("Error retrieving ballot votes. Please try again or contact IT Support if issue persists.", "Certificate of Appreciation");
            return EMPTY;
          })
         );
      })
    );

    this.subscriptions.add(
      sdBallotVotes$.subscribe({
        next: ({ user, projectDetailsResponse, votesResponse }) => {
          if (user === null ||
            !projectDetailsResponse.success || projectDetailsResponse.content === null ||
            !votesResponse.success || votesResponse.content === null) {
            this.hasPermissionToViewCoaPage = false;
            this.isExpired = null;
            this._toastService.showError("Error loading page. Please try again or contact IT Support if issue persists.", "Certificate of Appreciation Failed");
            return;
          }

          this.currentUser = user;
          this.projectDetails = projectDetailsResponse.content;
          this.ballotVotes = votesResponse.content;
          this.setPageAccess(this.currentUser, this.projectDetails, this.ballotVotes);
          this.isLoading = false
        },
        error: (err) => {
          console.error('Certificate of Appreciation: Error', err);
          this.resetPageAccess()
          this.isLoading = false
          this._toastService.showError("Error loading page. Please try again or contact IT Support if issue persists.", "Certificate of Appreciation Failed");
        },
        
      })
    );
  }


  resetPageAccess() {
    this.hasPermissionToViewCoaPage = null;
    this.isExpired = null;
    this.projectDetails = null;
    this.ballotVotes = [];
  }

  setPageAccess(currentUser: IUser, projectDetails: IProjectDetails, ballotVotes: ISdBallotVote[]) {

    const isProjectPublished = projectDetails.phaseId === appConstants.publicationStatus.published;

    if (!isProjectPublished || !projectDetails.publishedDate) {
      this.hasPermissionToViewCoaPage = false;
      this.isExpired = null;
      this._toastService.showError("Invalid project or link. Please reach out to your committee's point of contact for further support.", "Certificate of Appreciation");
      return;
    }


    // Check whether the CoA page is expired.
    // The page is only accessible within 14 days after the project is published.
    if (isValid(projectDetails.publishedDate)) {
      const startDate = new Date(projectDetails.publishedDate as Date);
      const endDate = new Date();
      const diffDays = differenceInCalendarDays(endDate, startDate);
      this.isExpired = diffDays > 14;
    }

    if (this.isExpired) {
      this._toastService.showError("This link has expired.  Please reach out to your committee's point of contact for further support.", "Certificate of Appreciation");
      return;
    }


    // Check whether user has permission to view CoA page.
    // To be eligible, user must be IsEligibleVoter on the ballot, CoOpted on the ballot, or Chairperson of the ballot.
    const committeeMember = ballotVotes.find(vote => vote.email?.toLowerCase() === currentUser.email?.toLowerCase());
    const isCmOnBallot = committeeMember !== undefined;
    const isEligibleVoter = committeeMember?.isEligible ?? false;
    const isCoOptedOrChairperson = 
      committeeMember?.role === CommitteeMemberRoles.CoOpted || 
      committeeMember?.role === CommitteeMemberRoles.Chairperson ||
      committeeMember?.role === CommitteeMemberRoles.ChairpersonElect;
    
    this.hasPermissionToViewCoaPage = isCmOnBallot && (isEligibleVoter || isCoOptedOrChairperson);
    //this.hasPermissionToViewCoaPage = true;

    if (!this.hasPermissionToViewCoaPage) {
      this._toastService.showError("You are not authorised to access this page. Please contact your committee's point of contact for further support.", "Certificate of Appreciation");
    }
  }

  setupSubmitSubscription() {
    this.subscriptions.add(
      this.submit$.pipe(
          map(() => {
            const request = this.coaForm.valid ?
              this.getCoaPreferenceRequest(this.coaForm.value) :
              null;

            return request;
          }),
          filter((request): request is ICoaPreferenceRequest => request != null),
          tap(() => this.isSubmitting = true),
          exhaustMap((request) => this._sdBallotService.saveCoaPreference(request).pipe(
            catchError(err => {
              this.isSubmitting = false;
              this._toastService.showError('There was an unexpected error. Please try again or contact Customer Success.', 'Your submission was unsuccessful');
              console.error(`CertificateOfPreference error: ${err}`);
              return EMPTY;
            })
          )),
      )
      .subscribe({
        next: (response: IApiResponse<any>) => {
          this.isSubmitting = false;
          if (response) {
            if (response.success) {
              this._toastService.showSuccess('Your request has ben submitted. Thank you!', 'Your submission was successful');
            } else {
              this._toastService.showError('There was an unexpected error. Please try again or contact Customer Success.', 'Your submission was unsuccessful');
              console.error(`CertificateOfPreference error: ${response.message}`)
            }

          }
        },
        error: (err: any) => {
          this.isSubmitting = false;
          this._toastService.showError('There was an unexpected error. Please try again or contact Customer Success.', 'Your submission was unsuccessful');
          console.error(`CertificateOfPreference error: ${err}`)
        }
      })
    );
  }

  submit() {
      this.submit$.next(true);
  }
  
  getCoaPreferenceRequest(formData: Partial<ICoaFormData>) : ICoaPreferenceRequest | null {

    if (this.projectDetails?.sdBallotId === undefined || this.projectDetails?.sdBallotId === null
      || this.projectDetails?.projectId === undefined || this.projectDetails?.projectId === null
    ) {
      return null;
    }

    const request: ICoaPreferenceRequest = {
      projectId: this.projectDetails?.projectId,
      ballotId: this.projectDetails?.sdBallotId,
      certificatePreference: formData.certificatePreferenceCtrl?? undefined,
      salutation: formData.salutationCtrl ?? undefined,
      firstName: formData.firstNameCtrl ?? undefined,
      lastName: formData.lastNameCtrl ?? undefined,
      address: formData.addressCtrl ?? undefined,
      apartmentSuite: formData.apartmentSuiteCtrl,
      suburbCity: formData.suburbCityCtrl ?? undefined,
      state: formData.stateCtrl ?? undefined,
      postcode: formData.postcodeCtrl ?? undefined,
      country: formData.countryCtrl ?? undefined,
    }
    return request;
  }
}

interface ICoaFormData {
    certificatePreferenceCtrl?: string | null,
    salutationCtrl?: string | null,
    firstNameCtrl?: string | null,
    lastNameCtrl?: string | null,
    addressCtrl?: string | null,
    apartmentSuiteCtrl?: string | null,
    suburbCityCtrl?: string | null,
    stateCtrl?: string | null,
    postcodeCtrl?: string | null,
    countryCtrl?: string | null,
}
